import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import TextArea from '../../components/Form/TextArea';
import Button from '../../components/Form/Button';
import { useCreateTransaction, useUpdateTransaction } from '../../hooks/useTransactions';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import {
  InTransactionDetailsSection,
  InAmountSection,
  InOtherAmountsSection,
  InDeductionSection,
  useInTransactionOptions,
  buildInTransactionPayload,
  validateInTransactionRates,
} from './InTransactionSections';
import type { InTransactionFormData, OtherAmountRow } from './InTransactionSections';
import { transactionToInFormValues, transactionToInOtherAmounts } from './transactionEditHelpers';

interface InTransactionFormProps {
  /** When provided, the form edits this transaction instead of creating one */
  transaction?: any;
  onSaved?: () => void;
  onCancel?: () => void;
}

const InTransactionForm: React.FC<InTransactionFormProps> = ({ transaction, onSaved, onCancel }) => {
  const isEdit = !!transaction;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [otherAmounts, setOtherAmounts] = useState<OtherAmountRow[]>(
    () => (transaction ? transactionToInOtherAmounts(transaction) : [])
  );
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(
    () => transaction?.invoiceHire || transaction?.invoice || null
  );

  const {
    currencies,
    currencyOptions,
    accountOptions,
    fetchInvoiceOptions,
    isLoading,
  } = useInTransactionOptions();

  const { execute: createTransaction, isLoading: createLoading } = useCreateTransaction(
    () => {
      setSubmitSuccess(true);
      setSubmitError(null);
      reset();
      setOtherAmounts([]);
      setSelectedInvoice(null);
      setTimeout(() => setSubmitSuccess(false), 3000);
    }
  );

  const { execute: updateTransaction, isLoading: updateLoading } = useUpdateTransaction(
    () => {
      setSubmitError(null);
      onSaved?.();
    }
  );

  const isSaving = createLoading || updateLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<InTransactionFormData>({
    defaultValues: transaction ? transactionToInFormValues(transaction) : {
      invoiceId: '', amount: 0, currency: '', exchangeRate: undefined,
      amountCollectedCurrency: '', amountCollectedRate: undefined,
      chequeDate: '', chequeNumber: '', bankName: '', depositAccount: '',
      deductionAmount: undefined, deductionCurrency: '', deductionRate: undefined,
      notes: '', transactionDate: new Date().toISOString().split('T')[0],
    }
  });

  const selectedCurrencyRaw = useWatch({ control, name: 'currency', defaultValue: '' });
  const selectedCollectedCurrencyRaw = useWatch({ control, name: 'amountCollectedCurrency', defaultValue: '' });
  const selectedInvoiceId = useWatch({ control, name: 'invoiceId', defaultValue: '' });
  const watchedAmount = useWatch({ control, name: 'amount', defaultValue: 0 });
  const watchedRate = useWatch({ control, name: 'exchangeRate' });
  const watchedDeductionCurrency = useWatch({ control, name: 'deductionCurrency', defaultValue: '' });
  const watchedDeductionAmount = useWatch({ control, name: 'deductionAmount' });
  const watchedDeductionRate = useWatch({ control, name: 'deductionRate' });

  const selectedCurrencyCode = useMemo(
    () => resolveCurrencyCode(selectedCurrencyRaw, currencies as any[]),
    [selectedCurrencyRaw, currencies]
  );
  const selectedCollectedCurrencyCode = useMemo(
    () => resolveCurrencyCode(selectedCollectedCurrencyRaw, currencies as any[]),
    [selectedCollectedCurrencyRaw, currencies]
  );
  const invoiceCurrencyCode = useMemo(
    () => (selectedInvoice ? resolveCurrencyCode(selectedInvoice.currency, currencies as any[]) : ''),
    [selectedInvoice, currencies]
  );
  const deductionCurrencyCode = useMemo(
    () => (watchedDeductionCurrency ? resolveCurrencyCode(watchedDeductionCurrency, currencies as any[]) : ''),
    [watchedDeductionCurrency, currencies]
  );

  // Edit mode: native selects render before their option lists load, so the
  // saved value falls back to the placeholder. Re-apply saved values once the
  // option lists are available (only once per field).
  const appliedRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    if (!isEdit) return;
    const saved = transactionToInFormValues(transaction);
    const apply = (field: keyof InTransactionFormData, ready: boolean, value?: string) => {
      if (appliedRef.current[field] || !value || !ready) return;
      setValue(field, value as any);
      appliedRef.current[field] = true;
    };
    const currenciesReady = Array.isArray(currencies) && currencies.length > 0;
    apply('currency', currenciesReady, saved.currency);
    apply('amountCollectedCurrency', currenciesReady, saved.amountCollectedCurrency);
    apply('deductionCurrency', currenciesReady, saved.deductionCurrency);
    apply('depositAccount', accountOptions.length > 0, saved.depositAccount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, transaction, currencies, accountOptions]);

  const handleInvoiceChange = useCallback((invoiceId: string) => {
    setValue('invoiceId', invoiceId);
  }, [setValue]);

  const handleInvoiceSelect = useCallback((invoice: any | null) => {
    setSelectedInvoice(invoice);
    if (invoice) {
      const invCurrency = invoice.currency as any;
      setValue('currency', typeof invCurrency === 'object' ? (invCurrency?.documentId || invCurrency?.id || '') : (invCurrency || ''));
      if (invoice.exchangeRate) {
        setValue('exchangeRate', invoice.exchangeRate);
      }
    }
  }, [setValue]);

  const addOtherAmount = () => {
    setOtherAmounts(prev => [
      ...prev,
      { id: Date.now().toString(), amount: 0, currency: '', exchangeRate: undefined }
    ]);
  };

  const removeOtherAmount = (id: string) => {
    setOtherAmounts(prev => prev.filter(item => item.id !== id));
  };

  const updateOtherAmount = (id: string, field: keyof OtherAmountRow, value: any) => {
    setOtherAmounts(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const onSubmit = async (data: InTransactionFormData) => {
    setSubmitError(null);
    const rateError = validateInTransactionRates(
      data, selectedCurrencyCode, invoiceCurrencyCode, deductionCurrencyCode);
    if (rateError) {
      setSubmitError(rateError);
      return;
    }
    try {
      const payload = buildInTransactionPayload(data, otherAmounts, currencies as any[], selectedInvoice);
      if (isEdit) {
        await updateTransaction({
          id: transaction.id,
          data: { ...payload, documentId: transaction.documentId },
        });
      } else {
        await createTransaction(payload);
      }
    } catch (error: any) {
      console.error('Error submitting transaction:', error);
      setSubmitError(error?.error?.message || error?.message || 'Error submitting transaction. Please try again.');
    }
  };

  return (
    <div className="card">
      <div className="px-8 py-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-2xl">
        <h2 className="text-2xl font-bold">{isEdit ? 'Edit In Transaction' : 'In Transaction'}</h2>
        <p className="text-green-100 mt-2">
          {isEdit ? 'Update this incoming payment' : 'Record an incoming payment / amount received'}
        </p>
      </div>

      {submitSuccess && (
        <div className="mx-8 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium">Transaction created successfully!</p>
        </div>
      )}

      {submitError && (
        <div className="mx-8 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-8">
        {isLoading && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">Loading form data...</p>
          </div>
        )}

        <InTransactionDetailsSection
          register={register}
          errors={errors}
          invoiceValue={selectedInvoiceId}
          onInvoiceChange={handleInvoiceChange}
          onInvoiceSelect={handleInvoiceSelect}
          fetchInvoiceOptions={fetchInvoiceOptions}
          selectedInvoice={selectedInvoice}
        />

        <InAmountSection
          register={register}
          errors={errors}
          currencyOptions={currencyOptions}
          accountOptions={accountOptions}
          selectedCurrencyRaw={selectedCurrencyRaw}
          selectedCurrencyCode={selectedCurrencyCode}
          selectedCollectedCurrencyRaw={selectedCollectedCurrencyRaw}
          selectedCollectedCurrencyCode={selectedCollectedCurrencyCode}
          invoiceCurrencyCode={invoiceCurrencyCode}
          amountValue={Number(watchedAmount) || 0}
          exchangeRateValue={Number(watchedRate) || 0}
        />

        <InOtherAmountsSection
          otherAmounts={otherAmounts}
          currencyOptions={currencyOptions}
          accountOptions={accountOptions}
          currencies={currencies}
          onAdd={addOtherAmount}
          onRemove={removeOtherAmount}
          onUpdate={updateOtherAmount}
        />

        <InDeductionSection
          register={register}
          errors={errors}
          currencyOptions={currencyOptions}
          hasInvoice={!!selectedInvoiceId}
          invoiceCurrencyCode={invoiceCurrencyCode}
          deductionCurrencyCode={deductionCurrencyCode}
          deductionAmountValue={Number(watchedDeductionAmount) || 0}
          deductionRateValue={Number(watchedDeductionRate) || 0}
        />

        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Additional Notes
          </h3>
          <TextArea
            label="Notes"
            {...register('notes')}
            error={errors.notes?.message}
            placeholder="Enter any additional notes about this transaction"
            rows={4}
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-end items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              if (isEdit) { onCancel?.(); return; }
              reset(); setOtherAmounts([]); setSelectedInvoice(null);
            }}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {isEdit ? 'Cancel' : 'Reset Form'}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving ? 'Processing...' : isEdit ? 'Update Transaction' : 'Submit Transaction'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InTransactionForm;
